import { useState } from 'react';

// Color palette for estado counters
const COLORS = [
  { bg: 'rgba(66, 195, 186, 0.08)', border: '#42C3BA', text: '#0d9488', barBg: 'linear-gradient(90deg, #42C3BA, #2dd4bf)' },
  { bg: 'rgba(59, 130, 246, 0.08)', border: '#3b82f6', text: '#2563eb', barBg: 'linear-gradient(90deg, #3b82f6, #60a5fa)' },
  { bg: 'rgba(245, 158, 11, 0.08)', border: '#f59e0b', text: '#d97706', barBg: 'linear-gradient(90deg, #f59e0b, #fbbf24)' },
  { bg: 'rgba(239, 68, 68, 0.08)', border: '#ef4444', text: '#dc2626', barBg: 'linear-gradient(90deg, #ef4444, #f87171)' },
  { bg: 'rgba(139, 92, 246, 0.08)', border: '#8b5cf6', text: '#7c3aed', barBg: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' },
  { bg: 'rgba(236, 72, 153, 0.08)', border: '#ec4899', text: '#db2777', barBg: 'linear-gradient(90deg, #ec4899, #f472b6)' },
  { bg: 'rgba(16, 185, 129, 0.08)', border: '#10b981', text: '#059669', barBg: 'linear-gradient(90deg, #10b981, #34d399)' },
  { bg: 'rgba(249, 115, 22, 0.08)', border: '#f97316', text: '#ea580c', barBg: 'linear-gradient(90deg, #f97316, #fb923c)' },
  { bg: 'rgba(6, 182, 212, 0.08)', border: '#06b6d4', text: '#0891b2', barBg: 'linear-gradient(90deg, #06b6d4, #22d3ee)' },
  { bg: 'rgba(148, 163, 184, 0.08)', border: '#94a3b8', text: '#64748b', barBg: 'linear-gradient(90deg, #94a3b8, #cbd5e1)' },
];

export default function DashboardEstadoChart({ stros, estados }) {
  const [activeEstado, setActiveEstado] = useState(null);

  // Count stros per estado
  const estadoCount = {};
  stros.forEach(s => {
    if (s.estados && Array.isArray(s.estados)) {
      s.estados.forEach(estId => {
        estadoCount[estId] = (estadoCount[estId] || 0) + 1;
      });
    } else if (s.estadoNuevo) {
      estadoCount[s.estadoNuevo] = (estadoCount[s.estadoNuevo] || 0) + 1;
    }
  });

  // Map estado IDs to names
  const estadoMap = {};
  if (estados && Array.isArray(estados)) {
    estados.forEach(e => {
      estadoMap[e.id] = e.nombre || e.estado || `Estado ${e.id}`;
    });
  }

  // Build sorted entries (all states)
  const entries = Object.entries(estadoCount)
    .map(([id, count]) => ({
      id: Number(id),
      name: estadoMap[Number(id)] || `Estado ${id}`,
      count,
    }))
    .sort((a, b) => b.count - a.count);

  // Filtered stros for the mini table
  let filteredStros = stros;
  if (activeEstado !== null) {
    filteredStros = stros.filter(s => {
      if (s.estados && Array.isArray(s.estados)) {
        return s.estados.includes(activeEstado);
      }
      // eslint-disable-next-line
      return s.estadoNuevo == activeEstado;
    });
  }

  // Sort filtered by vencimiento (most urgent first)
  const sortedFiltered = [...filteredStros].sort((a, b) => {
    if (!a.fechaVencimiento) return 1;
    if (!b.fechaVencimiento) return -1;
    return a.fechaVencimiento.localeCompare(b.fechaVencimiento);
  });

  const displayStros = sortedFiltered.slice(0, 50); // Show max 50

  const handleToggle = (estadoId) => {
    setActiveEstado(prev => (prev === estadoId ? null : estadoId));
  };

  const activeLabel = activeEstado !== null
    ? entries.find(e => e.id === activeEstado)?.name || 'Estado'
    : 'Todos';

  if (entries.length === 0) {
    return (
      <div className='dashboard-card'>
        <div className='dashboard-card-header'>
          <span className='dashboard-card-title'>
            <i className='bi bi-bar-chart-fill'></i>
            Siniestros por Estado
          </span>
        </div>
        <div className='dashboard-empty'>
          <i className='bi bi-inbox'></i>
          <p>Sin datos de estados</p>
        </div>
      </div>
    );
  }

  return (
    <div className='dashboard-card dashboard-estado-filtros-card'>
      <div className='dashboard-card-header'>
        <span className='dashboard-card-title'>
          <i className='bi bi-funnel-fill'></i>
          Siniestros por Estado
        </span>
        <span className='dashboard-card-badge'>
          {activeEstado !== null ? `${filteredStros.length} de ${stros.length}` : `${stros.length} siniestros`}
        </span>
      </div>

      {/* Estado counter chips */}
      <div className='estado-filtros-grid'>
        {/* "Todos" chip */}
        <button
          className={`estado-filtro-chip ${activeEstado === null ? 'estado-filtro-chip--active' : ''}`}
          onClick={() => setActiveEstado(null)}
          style={{
            '--chip-color': '#0f172a',
            '--chip-border': activeEstado === null ? '#42C3BA' : '#e2e8f0',
            '--chip-bg': activeEstado === null ? 'rgba(66, 195, 186, 0.08)' : 'white',
          }}
        >
          <span className='estado-filtro-count'>{stros.length}</span>
          <span className='estado-filtro-label'>Todos</span>
        </button>

        {entries.map((entry, idx) => {
          const color = COLORS[idx % COLORS.length];
          const isActive = activeEstado === entry.id;
          return (
            <button
              key={entry.id}
              className={`estado-filtro-chip ${isActive ? 'estado-filtro-chip--active' : ''}`}
              onClick={() => handleToggle(entry.id)}
              style={{
                '--chip-color': color.text,
                '--chip-border': isActive ? color.border : '#e2e8f0',
                '--chip-bg': isActive ? color.bg : 'white',
              }}
              title={entry.name}
            >
              <span className='estado-filtro-count'>{entry.count}</span>
              <span className='estado-filtro-label'>{entry.name}</span>
            </button>
          );
        })}
      </div>

      {/* Mini table */}
      <div className='estado-filtros-table-header'>
        <span className='estado-filtros-table-title'>
          <i className='bi bi-list-ul'></i>
          {activeLabel}
        </span>
        <span className='estado-filtros-table-count'>
          {filteredStros.length} siniestros
        </span>
      </div>
      <div className='estado-filtros-table-wrapper'>
        <table className='estado-filtros-table'>
          <thead>
            <tr>
              <th>Nro Stro</th>
              <th>Asegurado</th>
              <th>Estado</th>
              <th>Vencimiento</th>
            </tr>
          </thead>
          <tbody>
            {displayStros.map((s, idx) => {
              const estadoName = (() => {
                if (s.estados && Array.isArray(s.estados) && s.estados.length > 0) {
                  return estadoMap[s.estados[0]] || `Estado ${s.estados[0]}`;
                }
                return estadoMap[s.estadoNuevo] || '—';
              })();

              const today = new Date().toISOString().split('T')[0];
              const isVencido = s.fechaVencimiento && s.fechaVencimiento < today;

              return (
                <tr
                  key={s.id || idx}
                  className={isVencido ? 'estado-filtros-row--vencido' : ''}
                  onClick={() => {
                    if (s.id) window.open(`/#/${s.id}`, '_blank');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <td className='estado-filtros-nrostro'>{s.nroStro || '—'}</td>
                  <td className='estado-filtros-asegurado'>{s.nombre || '—'}</td>
                  <td>
                    <span className='estado-filtros-badge'>{estadoName}</span>
                  </td>
                  <td className={isVencido ? 'estado-filtros-vencido' : ''}>
                    {s.fechaVencimiento
                      ? new Date(s.fechaVencimiento + 'T00:00:00').toLocaleDateString('es-AR')
                      : '—'}
                  </td>
                </tr>
              );
            })}
            {displayStros.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                  Sin siniestros para mostrar
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
